import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import type { ObjPathProxy } from "Infrastructure/Translations/Resources";
import {
	getResourcePath,
	Resources,
} from "Infrastructure/Translations/Resources";
import i18n from "i18next";
import type { core } from "zod";

const DEFAULT_VALIDATION_MESSAGE = "Neplatná hodnota.";

/**
 * Translate a Validation resource key. If i18n hasn't initialized yet the
 * lookup would return the raw key (`"Validation:Generic"`); we pass an
 * unconditional `defaultValue` so the fallback is always a human sentence.
 */
const translateValidation = (
	resource: ObjPathProxy<ResourceDictionary, string>,
	options?: Record<string, unknown>,
): string =>
	i18n.t(getResourcePath(resource), {
		defaultValue: DEFAULT_VALIDATION_MESSAGE,
		...(options ?? {}),
	});

export const zodErrorMap: core.$ZodErrorMap = (issue) => {
	switch (issue.code) {
		case "invalid_type":
			if (issue.input === undefined || issue.input === null) {
				return { message: translateValidation(Resources.Validation.Required) };
			}
			return {
				message: translateValidation(Resources.Validation.InvalidFormat),
			};
		case "too_small": {
			if (issue.origin === "string") {
				return {
					message: translateValidation(Resources.Validation.String.Min, {
						count: Number(issue.minimum),
					}),
				};
			}
			if (issue.origin === "array") {
				return {
					message: translateValidation(Resources.Validation.Array.Min, {
						count: Number(issue.minimum),
					}),
				};
			}
			if (issue.origin === "number" || issue.origin === "bigint") {
				return {
					message: translateValidation(Resources.Validation.Number.Min, {
						count: Number(issue.minimum),
					}),
				};
			}
			return { message: translateValidation(Resources.Validation.Generic) };
		}
		case "too_big": {
			if (issue.origin === "string") {
				return {
					message: translateValidation(Resources.Validation.String.Max, {
						count: Number(issue.maximum),
					}),
				};
			}
			if (issue.origin === "number" || issue.origin === "bigint") {
				return {
					message: translateValidation(Resources.Validation.Number.Max, {
						count: Number(issue.maximum),
					}),
				};
			}
			return { message: translateValidation(Resources.Validation.Generic) };
		}
		case "invalid_format": {
			if (issue.format === "email") {
				return {
					message: translateValidation(Resources.Validation.InvalidEmail),
				};
			}
			return {
				message: translateValidation(Resources.Validation.InvalidFormat),
			};
		}
		case "invalid_value":
			return {
				message: translateValidation(Resources.Validation.InvalidFormat),
			};
		case "custom":
			return {
				message:
					issue.message ?? translateValidation(Resources.Validation.Generic),
			};
		default:
			return { message: translateValidation(Resources.Validation.Generic) };
	}
};
